import axios from 'axios';
import { get } from '@shared/axios';
import type { identifier } from '@core/models/base';
import type { CartDisplayModel, Cartv2DetailsDisplayModel } from '@shared/models/carts/carts';

import type { Cartv2PackageDisplayModel } from '@shared/models/carts/cartPackages';
import type { SubjectDisplayModel } from '@shared/models/subjects/subjects';
import type { CartV2OrderServiceDisplayModel } from '@shared/models/carts/cartOrderSevices';
import { fillAutoFiledPackageTextNodes } from '@apps/customer-package-builder/services/cartPackageModels.service';
import type { TextNodeFillOptions } from '@apps/customer-package-builder/services/cartPackageNode.service';
import { JobOrderMethod } from '@shared/models/subjects/accessCode';

export const fetchCart = async (jobId: identifier, orderMethod: JobOrderMethod) =>
  get<CartDisplayModel>(`/api/customer/jobs/${jobId}/cart?orderMethod=${orderMethod}`);

export const fetchCartDetails = async (jobId: identifier, orderMethod: JobOrderMethod) =>
  (await axios.get<Cartv2DetailsDisplayModel>(`/api/customer/jobs/${jobId}/cart/details?orderMethod=${orderMethod}`))
    .data;

export const fetchCartSubjects = async (jobId: identifier) =>
  (await axios.get<SubjectDisplayModel[]>(`/api/customer/jobs/${jobId}/cart/subjects`)).data;

export const deleteCart = async (jobId: identifier) => (await axios.delete(`/api/customer/jobs/${jobId}/cart`)).data;

export interface SubjectWithCartPackages_CartServices {
  subject: SubjectDisplayModel;
  cartPackages: Cartv2PackageDisplayModel[];
  cartServices: CartV2OrderServiceDisplayModel[];
}

/**
 * @param options Text-node fill behavior — pass `consumerTextNodeFillOptions(isV3)` so a V3 delivery
 * email is not treated as imported subject data, while legacy keeps its existing behavior.
 */
export const groupCartDetailsBySubject = (
  cartDetails: Cartv2DetailsDisplayModel | undefined,
  hasImportedData: boolean,
  options: TextNodeFillOptions = {},
): SubjectWithCartPackages_CartServices[] => {
  if (!cartDetails) return [];
  return cartDetails.subjects.map(
    (s) =>
      ({
        subject: s,
        cartPackages:
          cartDetails.cartPackages
            .filter((p) => p.subjectId == s.id)
            .map((p) => ({
              ...p,
              cartPackageItems: p.cartPackageItems.map((i) =>
                fillAutoFiledPackageTextNodes(s.fields, i, hasImportedData, options, s),
              ),
            })) || [],
        cartServices: cartDetails.cartOrderServicesDetails.cartOrderServices.filter((p) => p.subjectId == s.id) || [],
      }) as SubjectWithCartPackages_CartServices,
  );
};
